/**
 * View Preview Component
 * 
 * Renders MDX content with available components in a safe, sandboxed environment.
 * Supports real-time preview with error boundaries and loading states.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Backdrop,
  Button,
} from '@mui/material';
import { Refresh as RefreshIcon, Error as ErrorIcon } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { viewsApiService } from '../../services/views.api';
import { View, ViewRenderResponse } from '../../types/view.types';

// Layout components
import {
  FunnelLayout,
  Section,
  Grid,
  GridItem,
  MasonryGrid,
  Callout,
  InfoCallout,
  WarningCallout,
  ErrorCallout,
  SuccessCallout,
  TipCallout,
  ImportantCallout,
  Hero,
  KPI,
} from './layouts';

// Query components
import {
  ViewContentQuery,
  DownloadAll,
} from './queries';

const PreviewContainer = styled(Box)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
}));

const PreviewHeader = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  borderRadius: 0,
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: 48,
}));

const PreviewContent = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  // Center the preview content and constrain to standard page width
  display: 'flex',
  justifyContent: 'center',

  // MDX content styling
  '& h1': {
    fontSize: '2.5rem',
    fontWeight: 600,
    margin: theme.spacing(0, 0, 2),
    color: theme.palette.text.primary,
  },
  '& h2': {
    fontSize: '2rem',
    fontWeight: 500,
    margin: theme.spacing(3, 0, 1.5),
    color: theme.palette.text.primary,
  },
  '& h3': {
    fontSize: '1.5rem',
    fontWeight: 500,
    margin: theme.spacing(2, 0, 1),
    color: theme.palette.text.primary,
  },
  '& p': {
    fontSize: '1rem',
    lineHeight: 1.6,
    margin: theme.spacing(0, 0, 1.5),
    color: theme.palette.text.secondary,
  },
  '& ul, & ol': {
    margin: theme.spacing(0, 0, 1.5),
    paddingLeft: theme.spacing(3),
  },
  '& li': {
    margin: theme.spacing(0.5, 0),
    color: theme.palette.text.secondary,
  },
  '& code': {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.primary.main,
    padding: theme.spacing(0.25, 0.5),
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.875rem',
    fontFamily: '"Monaco", "Menlo", "Ubuntu Mono", monospace',
  },
  '& pre': {
    backgroundColor: theme.palette.grey[900],
    color: theme.palette.common.white,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    overflow: 'auto',
    margin: theme.spacing(1, 0, 2),
  },
  '& blockquote': {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    margin: theme.spacing(1, 0, 2),
    padding: theme.spacing(1, 0, 1, 2),
    backgroundColor: theme.palette.grey[50],
    fontStyle: 'italic',
  },
}));

// Wrapper for the actual preview content to constrain max-width
// This ensures the view content itself matches the standard page width
// even though the editor as a whole is wider to accommodate side panels
const PreviewContentWrapper = styled(Box)({
  width: '100%',
  maxWidth: '1200px', // Standard page width - matches Material-UI 'lg' breakpoint
});

interface ViewPreviewProps {
  viewId?: string;
  mdxContent: string;
  viewData?: Partial<View>;
  organizationId: string;
  teamId: string;
  onError?: (error: Error) => void;
}

export const ViewPreview: React.FC<ViewPreviewProps> = ({
  viewId,
  mdxContent,
  viewData,
  organizationId,
  teamId,
  onError,
}) => {
  const [renderedContent, setRenderedContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRenderTime, setLastRenderTime] = useState<string>('');

  // Available components for MDX rendering
  const components = useMemo(() => ({
    // Layout components
    FunnelLayout,
    Section,
    Grid,
    GridItem,
    MasonryGrid,
    Callout,
    InfoCallout,
    WarningCallout,
    ErrorCallout,
    SuccessCallout,
    TipCallout,
    ImportantCallout,
    Hero,
    KPI,
    
    // Query components
    ViewContentQuery,
    DownloadAll,
  }), []);

  // Render the MDX content
  const renderContent = async (forceRecompile = false) => {
    if (!mdxContent.trim()) {
      setRenderedContent('<div style="padding: 20px; text-align: center; color: #666;">No content to preview</div>');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      let result: ViewRenderResponse;

      if (viewId) {
        // Use the existing view render endpoint
        result = await viewsApiService.renderView(viewId, {
          organization_id: organizationId,
          team_id: teamId,
          force_recompile: forceRecompile,
        });
      } else {
        // For new views, we need to render the MDX content client-side
        // This is a simplified version - in production, you might want server-side rendering
        result = await renderMDXClientSide(mdxContent, components, organizationId, teamId);
      }

      setRenderedContent(result.html);
      setLastRenderTime(result.lastCompiled);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to render view';
      setError(errorMessage);
      onError?.(err instanceof Error ? err : new Error(errorMessage));
      console.error('Error rendering view:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Re-render when content changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      renderContent();
    }, 500); // Debounce rendering

    return () => clearTimeout(timeoutId);
  }, [mdxContent, viewId, organizationId, teamId]);

  const handleRefresh = () => {
    renderContent(true);
  };

  if (error) {
    return (
      <PreviewContainer>
        <PreviewHeader elevation={1}>
          <Typography variant="h6" color="error">
            Preview Error
          </Typography>
          <Button
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            variant="outlined"
            size="small"
          >
            Retry
          </Button>
        </PreviewHeader>
        <PreviewContent>
          <Alert
            severity="error"
            icon={<ErrorIcon />}
            action={
              <Button color="inherit" size="small" onClick={handleRefresh}>
                RETRY
              </Button>
            }
          >
            <Typography variant="body2">
              <strong>Rendering failed:</strong> {error}
            </Typography>
          </Alert>
        </PreviewContent>
      </PreviewContainer>
    );
  }

  return (
    <PreviewContainer>
      <PreviewHeader elevation={1}>
        <Typography variant="h6">
          Preview
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          {lastRenderTime && (
            <Typography variant="caption" color="text.secondary">
              Rendered: {new Date(lastRenderTime).toLocaleTimeString()}
            </Typography>
          )}
          <Button
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            variant="outlined"
            size="small"
            disabled={isLoading}
          >
            Refresh
          </Button>
        </Box>
      </PreviewHeader>

      <PreviewContent>
        {isLoading && (
          <Backdrop open sx={{ color: '#fff', zIndex: 1 }}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}

        <PreviewContentWrapper>
          <div
            dangerouslySetInnerHTML={{ __html: renderedContent }}
          />
        </PreviewContentWrapper>
      </PreviewContent>
    </PreviewContainer>
  );
};

/**
 * Client-side MDX rendering fallback
 * This is a simplified implementation for preview purposes
 */
async function renderMDXClientSide(
  mdxContent: string,
  components: Record<string, React.ComponentType<any>>,
  organizationId: string,
  teamId: string
): Promise<ViewRenderResponse> {
  try {
    // This is a basic markdown-to-HTML conversion
    // In a real implementation, you'd want proper MDX compilation
    let html = mdxContent;
    
    // Convert basic markdown to HTML
    html = html
      .replace(/^# (.+)$/gm, '<h1>$1</h1>')
      .replace(/^## (.+)$/gm, '<h2>$1</h2>')
      .replace(/^### (.+)$/gm, '<h3>$1</h3>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code>$1</code>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br>');
    
    // Wrap in paragraphs
    html = `<p>${html}</p>`;
    
    // Basic component replacement (this would be much more sophisticated in reality)
    html = html.replace(/<Hero[^>]*\/>/g, '<div class="hero-placeholder">Hero Component</div>');
    html = html.replace(/<Section[^>]*>/g, '<div class="section">');
    html = html.replace(/<\/Section>/g, '</div>');
    html = html.replace(/<ViewContentQuery[^>]*\/>/g, '<div class="content-query-placeholder">Content Query Component</div>');
    
    return {
      html,
      lastCompiled: new Date().toISOString(),
    };
  } catch (error) {
    throw new Error(`MDX compilation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

export default ViewPreview;